import { useState } from "react";
import { BrandName } from "@/shared/branding";
import { Link } from "react-router-dom";
import { Mountain, Mail, KeyRound, ArrowRight, Loader2, ArrowLeft } from "lucide-react";
import { forgotPassword } from "@/services/auth";
import { ApiError } from "@/services/api";

/**
 * PWRESET-1 · 忘记密码 · 申请页
 *
 * 输邮箱 → POST /auth/forgot-password → 后端无论邮箱存不存在都回同一句话（防枚举），
 * 所以这页提交成功后只有一种画面：「若账号存在已发送」。真正设密在 ResetPasswordPage
 * （邮件里的链接 /reset-password?token=）。
 *
 * 样式照 LoginPage / MfaEnrollPage 同一套卡片外壳（铁律十七）。
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await forgotPassword(email.trim());
      setSentMessage(res.message);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError("申请太频繁，请稍后再试");
      } else if (err instanceof ApiError) {
        setError(err.message || "申请失败");
      } else {
        setError("网络错误 · 请稍后重试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
            <Mountain className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-bold text-slate-900"><BrandName /></span>
            <span className="text-[12px] text-slate-500">运营后台 · v1</span>
          </div>
        </div>

        <div className="mb-1 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-blue-600" />
          <h1 className="text-xl font-bold text-slate-900">忘记密码</h1>
        </div>
        <p className="mb-6 text-[13px] text-slate-500">
          输入登录邮箱，我们会发一封带重置链接的邮件（30 分钟内有效）
        </p>

        {sentMessage ? (
          <div className="flex flex-col gap-4">
            <div
              data-testid="forgot-sent"
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[13px] font-medium text-emerald-700"
            >
              {sentMessage}
            </div>
            <p className="text-[12px] text-slate-500">
              没收到？检查垃圾邮件；60 秒后可再申请一次。若邮箱本身记不清了，请联系超级管理员在「权限管理」里重置。
            </p>
            <Link
              to="/login"
              className="flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[13px] font-medium text-slate-900 hover:bg-slate-50"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> 返回登录
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-slate-700">登录邮箱</span>
              <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
                <Mail className="h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="flex-1 bg-transparent text-[13px] outline-none"
                  autoFocus
                  required
                />
              </div>
            </label>

            {error && <div className="text-[12px] font-medium text-rose-600">{error}</div>}

            <button
              type="submit"
              disabled={submitting}
              data-testid="forgot-submit"
              className="mt-1 flex h-10 items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  发送重置邮件 <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>

            <Link to="/login" className="mt-1 text-center text-[12px] font-medium text-slate-500 hover:text-slate-900">
              返回登录
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
